import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { BrandLogo } from '@/components/ui/BrandLogo';
import { Container } from '@/components/ui/Container';
import { useWorkspace, type Workspace } from '@/features/auth/hooks/useWorkspace';
import {
  HEADER_LABELS,
  RESOURCE_LINKS,
  WHY_BUILT_IN,
  WHY_HIGHLIGHTS,
} from '@/features/marketing/content/header';
import { PRODUCT_NAV_GROUPS, featurePath } from '@/features/marketing/nav';
import { cn } from '@/lib/cn';

type MenuId = 'why' | 'products' | 'resources';

function MenuTrigger({
  label,
  isOpen,
  onOpen,
  onClose,
}: {
  label: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={isOpen}
      aria-haspopup="true"
      // Hovering (or the first tap) opens the menu. A mouse click lands right
      // after its own mouseenter already opened it, so only a keyboard
      // activation (event.detail === 0) is treated as a close toggle.
      onClick={(event) => {
        if (!isOpen) onOpen();
        else if (event.detail === 0) onClose();
      }}
      onMouseEnter={onOpen}
      className={cn(
        'flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
        isOpen ? 'bg-surface text-ink' : 'text-ink-soft hover:text-brand-600',
      )}
    >
      {label}
      <ChevronDown
        className={cn('size-3.5 transition-transform', isOpen && 'rotate-180')}
        aria-hidden
      />
    </button>
  );
}

/** Full-width dropdown panel anchored under the header row. */
function MenuPanel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className="absolute inset-x-0 top-full hidden border-b border-line bg-white shadow-lift lg:block">
      <Container className={cn('py-8', className)}>{children}</Container>
    </div>
  );
}

function WhyMenu() {
  return (
    <MenuPanel>
      <div className="grid gap-8 lg:grid-cols-[1fr_16rem]">
        <div className="grid gap-4 sm:grid-cols-3">
          {WHY_HIGHLIGHTS.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="group rounded-card border border-line bg-surface p-5 transition-colors hover:border-brand-300 hover:bg-brand-50/60"
            >
              <span className="grid size-10 place-items-center rounded-xl bg-white shadow-card">
                <item.icon className="size-5 text-brand-600" aria-hidden />
              </span>
              <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-ink">
                {item.title}
                <ArrowRight
                  className="size-3.5 text-brand-600 opacity-0 transition-opacity group-hover:opacity-100"
                  aria-hidden
                />
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted">{item.description}</p>
            </Link>
          ))}
        </div>

        <div className="border-line lg:border-l lg:pl-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">
            {HEADER_LABELS.builtInHeading}
          </p>
          <ul className="mt-4 space-y-4">
            {WHY_BUILT_IN.map((item) => (
              <li key={item.title}>
                <Link to={item.to} className="group flex items-start gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50">
                    <item.icon className="size-4.5 text-brand-600" aria-hidden />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-ink group-hover:text-brand-600">
                      {item.title}
                    </span>
                    <span className="block text-xs text-muted">{item.description}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/why-shoplaunch"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700"
          >
            {HEADER_LABELS.whyMoreLink}
            <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        </div>
      </div>
    </MenuPanel>
  );
}

/**
 * Nineteen links, kept to one short band.
 *
 * From `xl` one column per group, all five side by side, so the panel is only
 * as tall as the longest group instead of wrapping into a second band of
 * headings that pushed it down most of the viewport. Losing the bordered 32px
 * icon chips is what buys the narrower columns: they set both the row height
 * and the width a label like "Shipping & delivery" needed beside them. Below
 * `xl` five columns would wrap the longer labels, so it falls back to three.
 */
function ProductsMenu() {
  return (
    <MenuPanel className="py-6">
      <div className="grid grid-cols-3 gap-x-6 gap-y-5 xl:grid-cols-5 xl:gap-y-0">
        {PRODUCT_NAV_GROUPS.map((group) => (
          <div key={group.heading}>
            <p className="px-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-600">
              {group.heading}
            </p>
            <ul className="mt-1.5">
              {group.items.map((item) => (
                <li key={item.slug}>
                  <Link
                    to={featurePath(item.slug)}
                    className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface"
                  >
                    <item.icon
                      className="size-4 shrink-0 text-muted transition-colors group-hover:text-brand-600"
                      aria-hidden
                    />
                    <span className="whitespace-nowrap text-sm font-medium text-ink-soft transition-colors group-hover:text-ink">
                      {item.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </MenuPanel>
  );
}

function ResourcesMenu() {
  return (
    <MenuPanel>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {RESOURCE_LINKS.map((item) => (
          <Link
            key={item.title}
            to={item.to}
            className="group rounded-card border border-line bg-surface p-5 transition-colors hover:border-brand-300 hover:bg-brand-50/60"
          >
            <span className="grid size-10 place-items-center rounded-xl bg-white shadow-card">
              <item.icon className="size-5 text-brand-600" aria-hidden />
            </span>
            <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-ink">
              {item.title}
              <ArrowRight
                className="size-3.5 text-brand-600 opacity-0 transition-opacity group-hover:opacity-100 rtl-flip"
                aria-hidden
              />
            </p>
            <p className="mt-1 text-sm leading-relaxed text-muted">{item.description}</p>
          </Link>
        ))}
      </div>
    </MenuPanel>
  );
}

function MobileMenu({ workspace, onNavigate }: { workspace: Workspace; onNavigate: () => void }) {
  return (
    <div className="absolute inset-x-0 top-full max-h-[calc(100dvh-4rem)] overflow-y-auto border-b border-line bg-white shadow-lift lg:hidden">
      <Container className="py-4">
        <Link
          to="/why-shoplaunch"
          onClick={onNavigate}
          className="block rounded-lg px-2 py-2.5 text-sm font-semibold text-ink hover:bg-surface"
        >
          {HEADER_LABELS.whyMenu}
        </Link>

        <details className="group">
          <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg px-2 py-2.5 text-sm font-semibold text-ink hover:bg-surface [&::-webkit-details-marker]:hidden">
            {HEADER_LABELS.productsMenu}
            <ChevronDown
              className="size-4 text-muted transition-transform group-open:rotate-180"
              aria-hidden
            />
          </summary>
          <div className="space-y-4 px-2 pb-3 pt-1">
            {PRODUCT_NAV_GROUPS.map((group) => (
              <div key={group.heading}>
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                  {group.heading}
                </p>
                <ul className="mt-1.5">
                  {group.items.map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={featurePath(item.slug)}
                        onClick={onNavigate}
                        className="flex items-center gap-2.5 rounded-lg py-2 text-sm text-ink-soft hover:text-brand-600"
                      >
                        <item.icon className="size-4 text-muted" aria-hidden />
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>

        <Link
          to="/pricing"
          onClick={onNavigate}
          className="block rounded-lg px-2 py-2.5 text-sm font-semibold text-ink hover:bg-surface"
        >
          {HEADER_LABELS.pricing}
        </Link>
        <Link
          to="/enterprise"
          onClick={onNavigate}
          className="block rounded-lg px-2 py-2.5 text-sm font-semibold text-ink hover:bg-surface"
        >
          {HEADER_LABELS.enterprise}
        </Link>

        <details className="group">
          <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg px-2 py-2.5 text-sm font-semibold text-ink hover:bg-surface [&::-webkit-details-marker]:hidden">
            {HEADER_LABELS.resourcesMenu}
            <ChevronDown
              className="size-4 text-muted transition-transform group-open:rotate-180"
              aria-hidden
            />
          </summary>
          <ul className="px-2 pb-3 pt-1">
            {RESOURCE_LINKS.map((item) => (
              <li key={item.title}>
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  className="flex items-center gap-2.5 rounded-lg py-2 text-sm text-ink-soft hover:text-brand-600"
                >
                  <item.icon className="size-4 text-muted" aria-hidden />
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </details>

        <div className="mt-3 flex items-center gap-3 border-t border-line pt-4">
          {workspace.signedIn ? (
            <Link
              to={workspace.to}
              onClick={onNavigate}
              className="flex-1 rounded-xl bg-brand-500 px-4 py-2.5 text-center text-sm font-semibold text-white hover:bg-brand-600"
            >
              {workspace.label}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                onClick={onNavigate}
                className="flex-1 rounded-xl border border-line px-4 py-2.5 text-center text-sm font-semibold text-ink hover:bg-surface"
              >
                {HEADER_LABELS.login}
              </Link>
              <Link
                to="/register"
                onClick={onNavigate}
                className="flex-1 rounded-xl bg-brand-500 px-4 py-2.5 text-center text-sm font-semibold text-white hover:bg-brand-600"
              >
                {HEADER_LABELS.startTrial}
              </Link>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}

export function MarketingHeader() {
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const workspace = useWorkspace();

  // Any navigation closes whatever is open.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    if (!openMenu && !mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    // Tapping or clicking anywhere outside the header closes the menus - the
    // escape hatch for touch devices, which never fire mouseleave.
    const onPointerDown = (event: PointerEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openMenu, mobileOpen]);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-20 border-b border-line/80 bg-white/85 backdrop-blur"
      onMouseLeave={() => setOpenMenu(null)}
    >
      <Container className="flex h-16 items-center justify-between gap-6">
        <BrandLogo size="lg" withTagline />

        {/* Five items, a logo, a sign-in link and a call to action do not fit
            768px together - the row holds from `lg`, and below it everything
            but the call to action folds into the sheet. */}
        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          <MenuTrigger
            label={HEADER_LABELS.whyMenu}
            isOpen={openMenu === 'why'}
            onOpen={() => setOpenMenu('why')}
            onClose={() => setOpenMenu(null)}
          />
          <MenuTrigger
            label={HEADER_LABELS.productsMenu}
            isOpen={openMenu === 'products'}
            onOpen={() => setOpenMenu('products')}
            onClose={() => setOpenMenu(null)}
          />
          <Link
            to="/pricing"
            onMouseEnter={() => setOpenMenu(null)}
            className="rounded-lg px-2.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:text-brand-600"
          >
            {HEADER_LABELS.pricing}
          </Link>
          <Link
            to="/enterprise"
            onMouseEnter={() => setOpenMenu(null)}
            className="rounded-lg px-2.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:text-brand-600"
          >
            {HEADER_LABELS.enterprise}
          </Link>
          <MenuTrigger
            label={HEADER_LABELS.resourcesMenu}
            isOpen={openMenu === 'resources'}
            onOpen={() => setOpenMenu('resources')}
            onClose={() => setOpenMenu(null)}
          />
        </nav>

        <div className="flex items-center gap-3">
          {/* A merchant who is already signed in is shown their own way in
              rather than a door they walked through hours ago. While the boot
              refresh is still deciding, the anonymous pair stands: this site's
              primary call to action must not wait on a round trip that, for
              nearly everybody who reads this page, comes back empty. */}
          {workspace.signedIn ? (
            <Link
              to={workspace.to}
              className="hidden items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 sm:inline-flex"
            >
              {workspace.label}
              <ArrowRight className="size-4 rtl-flip" aria-hidden />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden text-sm font-medium text-ink-soft transition-colors hover:text-brand-600 sm:block"
              >
                {HEADER_LABELS.login}
              </Link>
              <Link
                to="/register"
                className="hidden items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 sm:inline-flex"
              >
                {HEADER_LABELS.startTrial}
                <ArrowRight className="size-4 rtl-flip" aria-hidden />
              </Link>
            </>
          )}
          <button
            type="button"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen((open) => !open)}
            className="grid size-10 place-items-center rounded-lg text-ink-soft hover:bg-surface lg:hidden"
          >
            {mobileOpen ? (
              <X className="size-5" aria-hidden />
            ) : (
              <Menu className="size-5" aria-hidden />
            )}
          </button>
        </div>
      </Container>

      {openMenu === 'why' && <WhyMenu />}
      {openMenu === 'products' && <ProductsMenu />}
      {openMenu === 'resources' && <ResourcesMenu />}
      {mobileOpen && <MobileMenu workspace={workspace} onNavigate={() => setMobileOpen(false)} />}
    </header>
  );
}
